import {
  cardId,
  createRng,
  IllegalMoveError,
  RANK_ACE,
  RANK_JACK,
  standardDeck,
  SUITS,
  type CardId,
  type GameEngine,
  type GameOptions,
  type RNG,
  type Seat,
} from "@bing/engine-core";
import { haftkhabisOpeningBeatMs, moveHoldMs } from "./pacing";
import {
  assertHaftkhabisSeats,
  EIGHT,
  handPoints,
  isPlainCard,
  isPlayable,
  nextSeat,
  rankOf,
  SEVEN,
  suitOf,
  TEN,
  TWO,
} from "./rules";
import {
  CATCH_HOLD_MAX_MS,
  CATCH_HOLD_MIN_MS,
  DEFAULT_TARGET_SCORE,
  EVENT_TAIL,
  HAFTKHABIS_RULES_VERSION,
  HAND_SIZE,
  PENALTY_STEP,
  type HaftkhabisEvent,
  type HaftkhabisEventBody,
  type HaftkhabisMove,
  type HaftkhabisPlay,
  type HaftkhabisRoundResult,
  type HaftkhabisScoreDetail,
  type HaftkhabisSideMove,
  type HaftkhabisState,
  type HaftkhabisView,
} from "./types";

const FULL_DECK: readonly CardId[] = standardDeck().map(cardId);

/**
 * How many specials one opening may turn back before the first plain card in the pile is simply taken.
 * A turn-back happens to ≈ 46 % of top cards, so 64 in a row never happens: the loop ends by code.
 */
const MAX_TURN_BACKS = 64;

function replaceAt<T>(items: readonly T[], index: number, value: T): T[] {
  return items.map((item, i) => (i === index ? value : item));
}

function withoutOwe(state: HaftkhabisState, seat: Seat): HaftkhabisState["owes"] {
  return state.owes.some((o) => o.seat === seat)
    ? state.owes.filter((o) => o.seat !== seat)
    : state.owes;
}

function withoutSafe(state: HaftkhabisState, seat: Seat): HaftkhabisState["safe"] {
  return state.safe.includes(seat) ? state.safe.filter((s) => s !== seat) : state.safe;
}

/** Append one event: the next `seq`, and the tail keeps its bound. */
function emit(state: HaftkhabisState, body: HaftkhabisEventBody): HaftkhabisState {
  const seq = state.eventSeq + 1;
  const kept =
    state.events.length >= EVENT_TAIL
      ? state.events.slice(state.events.length - EVENT_TAIL + 1)
      : state.events;
  return { ...state, events: [...kept, { seq, ...body } as HaftkhabisEvent], eventSeq: seq };
}

/**
 * Deal one round: 7 to each seat from the seat after the dealer, then D1: a turned special goes back
 * into the pile at a seeded random place (a fixed place would make where it sits public) and the next
 * is turned, until a plain card is up. Four hands can hold all 28 plain cards (≈ 1 deal in 10¹⁴),
 * which leaves a pile with none: the only deal that cannot start, so it is simply dealt again.
 */
function dealRound(
  seats: number,
  dealer: Seat,
  rng: RNG,
): { hands: CardId[][]; pile: CardId[]; top: CardId; turnedBack: CardId[] } {
  for (;;) {
    const deck = rng.shuffle(FULL_DECK);
    const hands: CardId[][] = Array.from({ length: seats }, () => []);
    for (let i = 0; i < seats; i++) {
      hands[(dealer + 1 + i) % seats] = deck.slice(i * HAND_SIZE, (i + 1) * HAND_SIZE);
    }
    const pile = deck.slice(seats * HAND_SIZE);
    if (!pile.some(isPlainCard)) continue;
    const turnedBack: CardId[] = [];
    while (!isPlainCard(pile[0]!)) {
      if (turnedBack.length >= MAX_TURN_BACKS) {
        const at = pile.findIndex(isPlainCard);
        const [plain] = pile.splice(at, 1);
        return { hands, pile, top: plain!, turnedBack };
      }
      const card = pile.shift()!;
      turnedBack.push(card);
      pile.splice(rng.int(pile.length + 1), 0, card); // 0 = the top … length = the bottom
    }
    const top = pile.shift()!;
    return { hands, pile, top, turnedBack };
  }
}

/** What carries from round to round. */
type MatchFields = Pick<
  HaftkhabisState,
  "rulesVersion" | "seats" | "totalScores" | "targetScore" | "lastRound" | "events" | "eventSeq"
>;

/** A fresh round: a new deal, the seat after the dealer on turn, the default direction (D2). */
function startRound(match: MatchFields, round: number, dealer: Seat, rng: RNG): HaftkhabisState {
  const { hands, pile, top, turnedBack } = dealRound(match.seats, dealer, rng);
  const first = (dealer + 1) % match.seats;
  const state: HaftkhabisState = {
    rulesVersion: match.rulesVersion,
    seats: match.seats,
    phase: "play",
    round,
    dealer,
    direction: 1,
    turn: first,
    hands,
    pile,
    discard: [],
    top,
    activeSuit: suitOf(top),
    penalty: 0,
    drawnCard: null,
    pendingTake: null,
    owes: [],
    safe: [],
    catchHoldMs: CATCH_HOLD_MIN_MS,
    moveCount: 0,
    totalScores: match.totalScores,
    targetScore: match.targetScore,
    lastRound: match.lastRound,
    events: match.events,
    eventSeq: match.eventSeq,
    rngState: rng.getState(),
  };
  return emit(state, { kind: "roundStart", round, dealer, first, top, turnedBack });
}

/** D16's «your next turn starts»: a NEW turn for `seat`, so a duty it still owes is over. */
function beginTurn(state: HaftkhabisState, seat: Seat): HaftkhabisState {
  return {
    ...state,
    phase: "play",
    turn: seat,
    drawnCard: null,
    pendingTake: null,
    owes: withoutOwe(state, seat),
  };
}

/** The turn moves on from `from`, `steps` seats the way it runs. */
function passTurn(state: HaftkhabisState, from: Seat, steps = 1): HaftkhabisState {
  let seat = from;
  for (let i = 0; i < steps; i++) seat = nextSeat(seat, state.direction, state.seats);
  return beginTurn(state, seat);
}

/**
 * D3: `seat` draws up to `count` off the pile. An empty pile first becomes the discards under the top,
 * shuffled (announced); with nothing there either, the draw gives what exists, possibly nothing. A hand
 * that changed ends that seat's duty and its press (D16).
 */
function drawCards(
  state: HaftkhabisState,
  seat: Seat,
  count: number,
): { state: HaftkhabisState; cards: CardId[] } {
  let next = state;
  let pile = state.pile;
  const cards: CardId[] = [];
  while (cards.length < count) {
    if (pile.length === 0) {
      if (next.discard.length === 0) break;
      const rng = createRng(next.rngState);
      pile = rng.shuffle(next.discard);
      next = emit(
        { ...next, discard: [], rngState: rng.getState() },
        { kind: "reshuffle", count: pile.length },
      );
    }
    cards.push(pile[0]!);
    pile = pile.slice(1);
  }
  if (cards.length === 0) return { state: { ...next, pile }, cards };
  return {
    state: {
      ...next,
      pile,
      hands: replaceAt(next.hands, seat, [...next.hands[seat]!, ...cards]),
      owes: withoutOwe(next, seat),
      safe: withoutSafe(next, seat),
    },
    cards,
  };
}

/**
 * A seat emptied its hand on `card`, so the round is over: everybody else scores what they hold, doubled
 * if `card` is a Jack (O). `card` is an 8 only in D9's corner ({@link applyDraw}). The match ends when a
 * total reaches the limit (`>=`); otherwise the deal passes one seat (D2). D20: totals only grow, the
 * winner's by nothing.
 */
function finishRound(state: HaftkhabisState, winner: Seat, card: CardId): HaftkhabisState {
  const doubled = rankOf(card) === RANK_JACK;
  // The winner's hand is empty: its 0 needs no special case (D20).
  const points = state.hands.map((hand) => handPoints(hand) * (doubled ? 2 : 1));
  const totalScores = state.totalScores.map((total, seat) => total + points[seat]!);
  const lastRound: HaftkhabisRoundResult = {
    round: state.round,
    winner,
    card,
    doubled,
    points,
    totalsAfter: totalScores,
    // The round's card shows them: public, the round is over. Sorted, as the discards are: a hand's order
    // is the deal's, the generator's output, and that is never exposed.
    hands: state.hands.map((hand) => [...hand].sort()),
  };
  const matchOver = totalScores.some((total) => total >= state.targetScore);
  const ended = emit(
    {
      ...state,
      totalScores,
      lastRound,
      penalty: 0,
      owes: [],
      safe: [],
      drawnCard: null,
      pendingTake: null,
    },
    {
      kind: "roundEnd",
      round: state.round,
      winner,
      card,
      doubled,
      points,
      totals: totalScores,
      matchOver,
    },
  );
  if (matchOver) return { ...ended, phase: "gameOver" };
  return startRound(
    ended,
    state.round + 1,
    (state.dealer + 1) % state.seats,
    createRng(ended.rngState),
  );
}

/** The seats a 2 played by `seat` can name. */
function otherSeats(seats: number, seat: Seat): Seat[] {
  return Array.from({ length: seats }, (_unused, s) => s).filter((s) => s !== seat);
}

function isOtherSeat(state: HaftkhabisState, seat: Seat, target: unknown): target is Seat {
  return (
    typeof target === "number" &&
    Number.isInteger(target) &&
    target >= 0 &&
    target < state.seats &&
    target !== seat
  );
}

/**
 * Every way to play `card`: a Jack once per suit (D6, D21), a 2 once per target (D21), neither when it
 * is your last card (D10). With exactly 2 cards each comes twice, with and without the press (D15).
 */
function playVariants(state: HaftkhabisState, seat: Seat, card: CardId): HaftkhabisPlay[] {
  const size = state.hands[seat]!.length;
  const rank = rankOf(card);
  let plays: HaftkhabisPlay[];
  if (size > 1 && rank === RANK_JACK) {
    plays = SUITS.map((suit) => ({ type: "play", card, suit }));
  } else if (size > 1 && rank === TWO) {
    plays = otherSeats(state.seats, seat).map((target) => ({ type: "play", card, target }));
  } else {
    plays = [{ type: "play", card }];
  }
  return size === 2 ? plays.flatMap((play) => [play, { ...play, tak: true }]) : plays;
}

function isLegalPlay(state: HaftkhabisState, seat: Seat, move: HaftkhabisPlay): boolean {
  const { phase } = state;
  if (phase !== "play" && phase !== "bonus" && phase !== "drawn") return false;
  const hand = state.hands[seat]!;
  if (!hand.includes(move.card)) return false;
  if (phase === "drawn") {
    if (move.card !== state.drawnCard) return false;
  } else if (!isPlayable(move.card, state.top, state.activeSuit, state.penalty)) {
    return false;
  }
  if (hand.length === 1) return true; // D10: a last card names nothing; a stray name is ignored
  const rank = rankOf(move.card);
  if (rank === RANK_JACK) return (SUITS as readonly unknown[]).includes(move.suit);
  if (rank === TWO) return isOtherSeat(state, seat, move.target);
  return true;
}

/** Whether `seat`, on turn, may draw: D4's «no legal card», or always under a penalty. */
function mayDraw(state: HaftkhabisState, seat: Seat): boolean {
  if (state.phase !== "play" && state.phase !== "bonus") return false;
  if (state.penalty > 0) return true;
  return !state.hands[seat]!.some((c) => isPlayable(c, state.top, state.activeSuit, 0));
}

/** The moves the rules give the seat on turn, before `catch`. Never empty: see the README. */
function turnMoves(state: HaftkhabisState, seat: Seat): HaftkhabisMove[] {
  if (state.phase === "take") return [{ type: "take" }];
  if (state.phase === "drawn") {
    return [...playVariants(state, seat, state.drawnCard!), { type: "pass" }];
  }
  const moves: HaftkhabisMove[] = [];
  for (const card of state.hands[seat]!) {
    if (isPlayable(card, state.top, state.activeSuit, state.penalty)) {
      moves.push(...playVariants(state, seat, card));
    }
  }
  if (moves.length === 0 || state.penalty > 0) moves.push({ type: "draw" });
  // D14: an 8-last's draw is a descent with no play to carry the press, so the draw carries it (D15, D17).
  if (state.hands[seat]!.length === 0) moves.push({ type: "draw", tak: true });
  return moves;
}

/**
 * D14: `seat` has just come to hold exactly one card. The press rode the move (D15), or it owes from the
 * event `seq` that did it, and D17's hold is drawn fresh for the seat that may catch it first.
 */
function descend(
  state: HaftkhabisState,
  seat: Seat,
  carriedPress: boolean,
  seq: number,
): HaftkhabisState {
  if (carriedPress) return emit({ ...state, safe: [...state.safe, seat] }, { kind: "press", seat });
  const rng = createRng(state.rngState);
  return {
    ...state,
    owes: [...state.owes, { seat, seq }],
    catchHoldMs: rng.range(CATCH_HOLD_MIN_MS, CATCH_HOLD_MAX_MS),
    rngState: rng.getState(),
  };
}

/** A card leaves the hand and lands on top, the press is settled, and its effect fires. */
function applyPlay(state: HaftkhabisState, seat: Seat, move: HaftkhabisPlay): HaftkhabisState {
  const { card } = move;
  const rest = state.hands[seat]!.filter((c) => c !== card);
  let next = emit(
    {
      ...state,
      hands: replaceAt(state.hands, seat, rest),
      discard: [...state.discard, state.top],
      top: card,
      activeSuit: suitOf(card),
      drawnCard: null,
      owes: withoutOwe(state, seat),
      safe: withoutSafe(state, seat),
    },
    { kind: "play", seat, card },
  );
  const playedSeq = next.eventSeq;
  const rank = rankOf(card);

  // D10: a last card ends the round at once; only the 8 owes something more (D9).
  if (rest.length === 0 && rank !== EIGHT) return finishRound(next, seat, card);

  // D14: this play left exactly one card: a duty, unless the press rode the move (D15).
  if (rest.length === 1) next = descend(next, seat, move.tak === true, playedSeq);

  switch (rank) {
    case SEVEN: {
      const penalty = next.penalty + PENALTY_STEP;
      return passTurn(emit({ ...next, penalty }, { kind: "seven", seat, penalty }), seat);
    }
    case EIGHT:
      return emit({ ...next, phase: "bonus", turn: seat }, { kind: "bonus", seat });
    case RANK_ACE: {
      const skipped = nextSeat(seat, next.direction, next.seats);
      return passTurn(emit(next, { kind: "skip", seat, skipped }), seat, 2);
    }
    case TEN: {
      const direction = next.direction === 1 ? -1 : 1;
      return passTurn(emit({ ...next, direction }, { kind: "reverse", seat, direction }), seat);
    }
    case RANK_JACK: {
      const suit = move.suit!;
      return passTurn(emit({ ...next, activeSuit: suit }, { kind: "suit", seat, suit }), seat);
    }
    case TWO: {
      const target = move.target!; // `isLegalPlay`: a 2 that leaves you a card names its seat
      if (rest.length >= 2) {
        return emit(
          { ...next, phase: "take", turn: target, pendingTake: { giver: seat, target } },
          { kind: "target", seat, target, via: "take" },
        );
      }
      // O: the take would leave you without a card, so the target draws from the pile instead.
      const named = emit(next, { kind: "target", seat, target, via: "draw" });
      const drawn = drawCards(named, target, 1);
      return passTurn(
        emit(drawn.state, {
          kind: "draw",
          seat: target,
          count: drawn.cards.length,
          reason: "two",
          cards: drawn.cards,
        }),
        seat,
      );
    }
    default:
      return passTurn(next, seat);
  }
}

/**
 * D5, or O's plain draw and D8's bonus draw. An empty hand here is an 8 played as a last card: the draw
 * that fills it is a descent (D14), and with nothing to draw (D9's corner) the owed draw cannot be met, so
 * the round ends with this seat as its winner: no seat ever sits at zero cards with the round open. (The
 * card the 8 went on lies under it, so a catch in that window is the only thing that can have spent it.)
 */
function applyDraw(state: HaftkhabisState, seat: Seat, carriedPress: boolean): HaftkhabisState {
  if (state.penalty > 0) {
    const owed = state.penalty;
    const drawn = drawCards(state, seat, owed);
    const paid = emit(
      { ...drawn.state, penalty: 0 },
      { kind: "penaltyPaid", seat, owed, count: drawn.cards.length, cards: drawn.cards },
    );
    return passTurn(paid, seat);
  }
  const reason = state.phase === "bonus" ? "bonus" : "turn";
  const fromEmpty = state.hands[seat]!.length === 0;
  const drawn = drawCards(state, seat, 1);
  const logged = emit(drawn.state, {
    kind: "draw",
    seat,
    count: drawn.cards.length,
    reason,
    cards: drawn.cards,
  });
  const card = drawn.cards[0];
  if (card === undefined && fromEmpty) return finishRound(logged, seat, logged.top);
  const next =
    card !== undefined && fromEmpty ? descend(logged, seat, carriedPress, logged.eventSeq) : logged;
  if (card !== undefined && isPlayable(card, next.top, next.activeSuit, 0)) {
    return { ...next, phase: "drawn", drawnCard: card };
  }
  return passTurn(next, seat);
}

/** D13: the blind take. The tap names nothing: the card is uniform, from the carried generator. */
function applyTake(state: HaftkhabisState): HaftkhabisState {
  const { giver, target } = state.pendingTake!;
  const rng = createRng(state.rngState);
  const giverHand = state.hands[giver]!;
  const card = giverHand[rng.int(giverHand.length)]!;
  const hands = state.hands.map((hand, seat) => {
    if (seat === giver) return hand.filter((c) => c !== card);
    return seat === target ? [...hand, card] : hand;
  });
  const owes = state.owes.filter((o) => o.seat !== giver && o.seat !== target);
  const safe = state.safe.filter((s) => s !== giver && s !== target);
  const taken = emit(
    { ...state, hands, owes, safe, rngState: rng.getState() },
    { kind: "take", giver, target, card },
  );
  return passTurn(taken, giver);
}

/**
 * THE SIDE DOOR's two moves are recognised by type alone, so a door can route them before it asks
 * anything else. `press` and `catch` come from any seat, on turn or not, at any time.
 */
export function isSideMove(move: HaftkhabisMove): move is HaftkhabisSideMove {
  return move.type === "press" || move.type === "catch";
}

/**
 * Whether `seat` may make this out-of-turn move now: read off `owes`, which a round's end empties, so
 * nothing can be pressed or caught after a match. The 2 s grace is the server door's, not this.
 */
export function isSideMoveLegal(
  state: HaftkhabisState,
  seat: Seat,
  move: HaftkhabisSideMove,
): boolean {
  if (move.type === "press") return state.owes.some((o) => o.seat === seat);
  if (move.type !== "catch" || move.target === seat) return false;
  return state.owes.some((o) => o.seat === move.target);
}

/**
 * Apply an out-of-turn move (README: the side door). It never moves the turn, the phase, the penalty
 * or the direction: D16's «a catch during your own 8 bonus or take leaves that phase in force» is
 * therefore true of every phase. Pure. Throws {@link IllegalMoveError} when not legal.
 */
export function applySideMove(
  state: HaftkhabisState,
  seat: Seat,
  move: HaftkhabisSideMove,
): HaftkhabisState {
  if (!isSideMoveLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
  if (move.type === "press") {
    return emit(
      { ...state, owes: withoutOwe(state, seat), safe: [...state.safe, seat] },
      { kind: "press", seat },
    );
  }
  const { target } = move;
  const drawn = drawCards(state, target, 1);
  return emit(
    { ...drawn.state, owes: withoutOwe(drawn.state, target) },
    { kind: "catch", by: seat, target, count: drawn.cards.length, cards: drawn.cards },
  );
}

/**
 * D18 + D22 for the server's lapse: a timer lapse plays one real action and never a catch, so a turn
 * without an 8 keeps its two windows at most. The door hands the lapse's bot this list, and the bots
 * catch only when `catch` is on the list they are given. Pure: drops `catch`, keeps the rest in order.
 */
export function lapseMoves(legal: readonly HaftkhabisMove[]): HaftkhabisMove[] {
  return legal.filter((move) => move.type !== "catch");
}

/** The engine half of resuming a save: TRUE only for a state made under these rules. */
export function isCurrentHaftkhabisRules(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { rulesVersion?: unknown }).rulesVersion === HAFTKHABIS_RULES_VERSION
  );
}

/** One seat's copy of an event: hidden cards are dropped for everybody who may not see them. */
function eventFor(event: HaftkhabisEvent, viewer: Seat): HaftkhabisEvent {
  switch (event.kind) {
    case "draw":
    case "penaltyPaid": {
      if (event.seat === viewer) return event;
      const { cards: _hidden, ...rest } = event;
      return rest;
    }
    case "catch": {
      if (event.target === viewer) return event;
      const { cards: _hidden, ...rest } = event;
      return rest;
    }
    case "take": {
      if (event.giver === viewer || event.target === viewer) return event;
      const { card: _hidden, ...rest } = event;
      return rest;
    }
    default:
      return event;
  }
}

/** The round as the match's record keeps it: `hands` are for the table, and stay off a row that is kept. */
function roundRecord(result: HaftkhabisRoundResult | null): HaftkhabisScoreDetail["lastRound"] {
  if (result === null) return null;
  const { hands: _shown, ...record } = result;
  return record;
}

export const haftkhabisEngine: GameEngine<HaftkhabisState, HaftkhabisMove, HaftkhabisView> = {
  gameId: "haftkhabis",

  canResume: isCurrentHaftkhabisRules,
  isSideMove,
  isSideMoveLegal,
  applySideMove,

  createInitialState(opts: GameOptions, rng: RNG): HaftkhabisState {
    const { seats } = opts;
    assertHaftkhabisSeats(seats);
    const target = opts.rules?.["targetScore"];
    const match: MatchFields = {
      rulesVersion: HAFTKHABIS_RULES_VERSION,
      seats,
      totalScores: Array.from({ length: seats }, () => 0),
      targetScore:
        typeof target === "number" && Number.isFinite(target) && target > 0
          ? target
          : DEFAULT_TARGET_SCORE,
      lastRound: null,
      events: [],
      eventSeq: 0,
    };
    return startRound(match, 0, rng.int(seats), rng);
  },

  /**
   * Only the seat on turn ever has moves, and never none: the rooms' turn windows and bots both find
   * the actor by asking this of every seat (a dead table otherwise).
   */
  legalMoves(state: HaftkhabisState, seat: Seat): HaftkhabisMove[] {
    if (state.phase === "gameOver" || seat !== state.turn) return [];
    const moves = turnMoves(state, seat);
    // D17: a bot's own step may catch before it acts. `press` is never here: side door only.
    for (const owe of state.owes) {
      if (owe.seat !== seat) moves.push({ type: "catch", target: owe.seat });
    }
    return moves;
  },

  isLegal(state: HaftkhabisState, seat: Seat, move: HaftkhabisMove): boolean {
    if (state.phase === "gameOver" || seat !== state.turn) return false;
    switch (move.type) {
      case "play":
        return isLegalPlay(state, seat, move);
      case "draw":
        return mayDraw(state, seat);
      case "pass":
        return state.phase === "drawn";
      case "take":
        return state.phase === "take";
      case "catch":
        return isSideMoveLegal(state, seat, move);
      default:
        return false;
    }
  },

  applyMove(state: HaftkhabisState, seat: Seat, move: HaftkhabisMove): HaftkhabisState {
    if (!haftkhabisEngine.isLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
    // D17: every step spends the hold; only a NEW descent, in `applyPlay`, draws a fresh one.
    const stepped: HaftkhabisState = { ...state, catchHoldMs: CATCH_HOLD_MIN_MS };
    if (move.type === "catch") return applySideMove(stepped, seat, move);
    const acted: HaftkhabisState = { ...stepped, moveCount: state.moveCount + 1 };
    switch (move.type) {
      case "play":
        return applyPlay(acted, seat, move);
      case "draw":
        return applyDraw(acted, seat, move.tak === true);
      case "pass":
        return passTurn(emit(acted, { kind: "pass", seat }), seat);
      case "take":
        return applyTake(acted);
      default:
        throw new IllegalMoveError(seat, move);
    }
  },

  isTerminal(state: HaftkhabisState): boolean {
    return state.phase === "gameOver";
  },

  /**
   * D19: the fewest minus points win, and a tie is exactly بی‌دل's: every seat on the minimum is listed
   * and `tied` is true. Meaningful at `gameOver`; mid-match it reads the totals so far.
   */
  score(state: HaftkhabisState) {
    const seatScores = [...state.totalScores];
    const best = Math.min(...seatScores);
    const winners = seatScores.flatMap((total, seat) => (total === best ? [seat] : []));
    const detail: HaftkhabisScoreDetail = {
      roundsPlayed: state.lastRound === null ? 0 : state.lastRound.round + 1,
      targetScore: state.targetScore,
      lastRound: roundRecord(state.lastRound),
    };
    return { seatScores, winners, tied: winners.length > 1, detail };
  },

  /**
   * The rooms' pacing hint. The round's opening waits for the deal. D17: while another seat owes, the
   * next step waits for the hold, so a human who just descended never loses a race to a bot: a fresh
   * descent's 3–6 s, and 3 s on any later step while it is still owed (the engine can't tell a bot that
   * declined from a human who moved at once, and a bot behind that human must still wait).
   * Otherwise undefined: the rooms' flat cadence. Pure: reads `state` only.
   */
  postMoveDelayMs(state: HaftkhabisState): number | undefined {
    if (state.phase === "gameOver") return undefined;
    const beat = haftkhabisOpeningBeatMs(state);
    if (beat !== null) return beat;
    // what the screen still draws of the last move: a bot never outruns it (`pacing.ts`)…
    const move = moveHoldMs(state.events);
    // …and D17's hold, a human's chance to press before a bot's catch, whichever is the longer
    if (!state.owes.some((o) => o.seat !== state.turn)) return move;
    return Math.max(state.catchHoldMs, move ?? 0);
  },

  viewFor(state: HaftkhabisState, seat: Seat): HaftkhabisView {
    return {
      seats: state.seats,
      phase: state.phase,
      you: seat,
      round: state.round,
      dealer: state.dealer,
      direction: state.direction,
      turn: state.turn,
      hand: [...state.hands[seat]!],
      handCounts: state.hands.map((hand) => hand.length),
      top: state.top,
      activeSuit: state.activeSuit,
      penalty: state.penalty,
      pileCount: state.pile.length,
      discardCount: state.discard.length,
      discard: [...state.discard].sort(),
      drawnCard: state.turn === seat ? state.drawnCard : null,
      pendingTake: state.pendingTake,
      owes: state.owes,
      safe: state.safe,
      moveCount: state.moveCount,
      totalScores: [...state.totalScores],
      targetScore: state.targetScore,
      lastRound: state.lastRound,
      events: state.events.map((event) => eventFor(event, seat)),
      eventSeq: state.eventSeq,
    };
  },
};
